import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, CircleAlertIcon, MoreIcon } from '@persona-id/design-tokens/icons/24px';
import { keyBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import LegacyTooltip from 'dashboard/components/Tooltip';
import { Account } from 'dashboard/models/account';
import { AccountAction } from 'dashboard/models/account-action';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountVariableDefinition } from 'dashboard/models/account-variable-definition';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { TemplateFilterOption } from 'dashboard/models/template';
import { TriggerAccount } from 'dashboard/models/trigger';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { AccountDataContextProvider } from 'dashboard/routes/DashboardAccountsShowRoute/useAccountDataContext';
import { Z_INDICES } from 'dashboard/styles/variables';
import AccountActionModal from 'dashboard/views/DashboardAccountsShowView/components/AccountActionModal';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';
import { Json } from 'lib/types';

const HeaderContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled('div')`
  display: flex;
  flex-direction: row;
`;

const BackContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const LinkTooltip = styled(LegacyTooltip).attrs(() => ({
  placement: 'left',
}))`
  margin-top: ${Spacing[4]};
  padding: ${Spacing[2]};
  max-width: 256px;
  z-index: ${Z_INDICES.topNavTooltip};
`;

const DisabledTooltipIconContainer = styled('div')`
  cursor: pointer;
  pointer-events: auto;

  svg path {
    fill: ${Color.CoreSlate700};
  }
  svg {
    vertical-align: middle;
  }
`;

interface AccountHeaderProps {
  account: Account;
  accountStatuses: AccountStatus[];
  accountActions: AccountAction[];
  accountVariableDefinitions: AccountVariableDefinition[];
  visibleActionIds?: string[];
  actionWorkflowVersions: WorkflowVersion[];
  featureFlags: FeatureFlagsMap;
  triggers: TriggerAccount[];
  showCreateInquiryModal: () => void;
  showUpdateAccountModal: () => void;
  showRedactAccountModal: () => void;
  showCreateReportModal: () => void;
  showCreateCaseModal: () => void;
  currentEnvironment: Environment;
  isCreateReportButtonDisabled: boolean;
  disabledReportReason?: string;
  isLoadingAccountActions: boolean;
  isLoadingReportTemplates: boolean;
  inquiryTemplateFilterOptions: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  handleRunAccountAction: (values: Json, accountActionId: string) => Promise<void>;
  handleUpdateAccountStatus: (accountStatusId: string) => Promise<void>;
}

const AccountHeader = ({
  account,
  accountStatuses,
  accountActions,
  accountVariableDefinitions,
  visibleActionIds,
  actionWorkflowVersions,
  featureFlags,
  triggers,
  handleUpdateAccountStatus,
  showCreateInquiryModal,
  showUpdateAccountModal,
  showRedactAccountModal,
  showCreateReportModal,
  showCreateCaseModal,
  currentEnvironment,
  isCreateReportButtonDisabled,
  disabledReportReason = 'Not provided',
  isLoadingAccountActions,
  isLoadingReportTemplates,
  inquiryTemplateFilterOptions,
  handleRunAccountAction,
}: AccountHeaderProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedAccountAction, setSelectedAccountAction] = useState<AccountAction>(undefined);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedTriggerAccount, setSelectedTriggerAccount] = useState<TriggerAccount>(undefined);

  const [accountActionModal, { enable: showAccountActionModal, disable: hideAccountActionModal }] =
    useToggle(false);
  const triggerAccountMap = keyBy('id', triggers);
  const actionWorkflowVersionMap = keyBy('id', actionWorkflowVersions);
  const canEditAccount = usePermission(Permission.AccountWrite);

  const accountActionItems = useMemo(() => {
    if (isLoadingAccountActions) {
      return [];
    }

    const handleClickRunActionButton = (accountAction: AccountAction) => {
      const latestPublishedVersion =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        actionWorkflowVersionMap[accountAction.relationships['latest-published-version'].data.id];
      const trigger = triggerAccountMap[latestPublishedVersion.relationships.trigger.data.id];

      if (trigger.attributes['input-modal-configuration'].length === 0) {
        handleRunAccountAction({}, accountAction.id);
      } else {
        setSelectedAccountAction(accountAction);
        setSelectedTriggerAccount(trigger);
        showAccountActionModal();
      }
    };

    const visibleAccountActions =
      visibleActionIds == null || visibleActionIds.length === 0
        ? accountActions
        : accountActions.filter((action) => visibleActionIds.includes(action.id));

    return (visibleAccountActions || []).map((action) => ({
      id: `account-header__button--account-action-${action.id}`,
      'data-test': `account-header__button--account-action-${action.id}`,
      name: `account-header__button--account-action-${action.id}`,
      onClick: () => handleClickRunActionButton(action),
      label: action.attributes.name,
      variant: 'basic' as const,
      hidden: false,
      disabled: !canEditAccount,
      disabledTooltip: permissionErrorString(Permission.AccountWrite),
    }));
  }, [
    isLoadingAccountActions,
    canEditAccount,
    accountActions,
    actionWorkflowVersionMap,
    triggerAccountMap,
    visibleActionIds,
    showAccountActionModal,
    handleRunAccountAction,
  ]);

  const moreItems = [
    {
      id: 'account-header__button--update-account',
      name: 'account-header__button--update-account',
      'data-test': 'account-header__button--update-account',
      onClick: () => showUpdateAccountModal(),
      label: 'Update account',
      disabled: !usePermission(Permission.AccountWrite),
      disabledTooltip: permissionErrorString(Permission.AccountWrite),
    },
    {
      id: 'account-header__button--delete-account',
      name: 'account-header__button--delete-account',
      'data-test': 'account-header__button--delete-account',
      onClick: () => showRedactAccountModal(),
      label: 'Redact',
      variant: 'danger' as const,
      disabled: !usePermission(Permission.AccountRedact),
      disabledTooltip: permissionErrorString(Permission.AccountRedact),
    },
    ...accountActionItems,
  ];

  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;

  const createInquiryButton = (
    <>
      <Button
        id='account-header__button--create-inquiry'
        onClick={showCreateInquiryModal}
        disabled={!isCurrentEnvironmentActive || inquiryTemplateFilterOptions.length === 0}
        variant='basic'
        aria-label='Create inquiry'
      >
        Create inquiry
      </Button>
    </>
  );

  const accountStatusItems = accountStatuses
    .filter((accountStatus) => {
      // Only show an archived account status if that is the current account status
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        accountStatus.id === account.relationships['account-status'].data.id ||
        !accountStatus.attributes['archived-at']
      );
    })
    .map((accountStatus) => {
      return {
        id: accountStatus.id,
        label: accountStatus.attributes.name,
      };
    });

  const accountStatus = accountStatuses.find((accountStatus) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return accountStatus.id === account.relationships['account-status'].data.id;
  });

  return (
    <>
      <HeaderContainer>
        <HeaderRow>
          <BackContainer>
            <NavBack to='/dashboard/accounts'>All accounts</NavBack>
          </BackContainer>
          <HStack>
            {featureFlags['dashboard:accounts-show-account-status'] && accountStatus != null && (
              <Permissioned permission={Permission.AccountWrite} fallback={null}>
                <DropdownMenu
                  trigger={
                    <Button
                      id='account-header-status__button--dropdown'
                      tabIndex={0}
                      variant='basic'
                      width='auto'
                      suffixIcon={<CaretDownIcon />}
                      aria-label='Update account status'
                    >
                      Status: {accountStatus.attributes.name}
                    </Button>
                  }
                  align='center'
                >
                  {accountStatusItems.map((item) => (
                    <DropdownMenu.Item
                      id={item.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUpdateAccountStatus(item.id);
                      }}
                      key={item.id}
                    >
                      {item.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu>
              </Permissioned>
            )}
            <Permissioned
              permission={[Permission.InquiryWrite, Permission.InquiryCreate]}
              fallback={null}
            >
              {createInquiryButton}
            </Permissioned>
            <LinkTooltip
              tooltip={disabledReportReason}
              // This disables the tooltip, not the button.
              disabled={!isCreateReportButtonDisabled}
            >
              <Button
                id='account-header__button--create-report'
                onClick={showCreateReportModal}
                disabled={isCreateReportButtonDisabled}
                isPending={isLoadingReportTemplates}
                variant='basic'
                aria-label='Create report'
              >
                Create report
              </Button>
            </LinkTooltip>
            <LinkTooltip
              tooltip={permissionErrorString(Permission.CaseCreate)}
              // This disables the tooltip, not the button.
              disabled={usePermission(Permission.CaseCreate)}
            >
              <Button
                id='account-header__button--create-case'
                onClick={showCreateCaseModal}
                disabled={!usePermission(Permission.CaseCreate)}
                variant='basic'
                aria-label='Create case'
              >
                Create case
              </Button>
            </LinkTooltip>
            <DropdownMenu
              trigger={
                <Button
                  id='account-header__button--dropdown'
                  tabIndex={0}
                  variant='basic'
                  prefixIcon={<MoreIcon />}
                  aria-label='More actions'
                >
                  More
                </Button>
              }
              align='end'
            >
              {moreItems.map((item) => (
                <DropdownMenu.Item
                  data-test={item['data-test']}
                  id={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.onClick();
                  }}
                  key={item.id}
                  aria-label={item.label}
                  variant={item.variant}
                  disabled={item.disabled}
                  suffixIcon={
                    item.disabled &&
                    item.disabledTooltip && (
                      <Tooltip
                        hideArrow
                        side='bottom'
                        trigger={
                          <DisabledTooltipIconContainer>
                            <CircleAlertIcon size={16} />
                          </DisabledTooltipIconContainer>
                        }
                        zIndex={Z_INDICES.toast + Z_INDICES.tooltip}
                      >
                        <Paragraph color='primary' style={{ width: '350px' }}>
                          {item.disabledTooltip}
                        </Paragraph>
                      </Tooltip>
                    )
                  }
                >
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </HStack>
        </HeaderRow>
      </HeaderContainer>
      <ModalWrapper isOpen={accountActionModal} handleCloseModal={hideAccountActionModal}>
        <AccountDataContextProvider
          accountVariableDefinitions={accountVariableDefinitions}
          accountId={account.id}
        >
          {(data) => (
            <AccountActionModal
              accountDataContext={data}
              accountAction={selectedAccountAction}
              trigger={selectedTriggerAccount}
              handleRunAccountAction={handleRunAccountAction}
              hideModal={hideAccountActionModal}
            />
          )}
        </AccountDataContextProvider>
      </ModalWrapper>
    </>
  );
};

export default AccountHeader;
